import { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { 
  ChartBar, 
  Home, 
  Brain, 
  Stethoscope, 
  Beaker, 
  Target, 
  Search, 
  FileText, 
  ClipboardList, 
  FileStack, 
  ClipboardCopy,
  FileCheck,
  Pen,
  ScanEye,
  Eye,
  AudioLines,
  FileBarChart
} from 'lucide-react';
import { useGetData } from '../../../../hooks/useGetData';
import { EvaluadoInterface } from '../../../../interfaces/EvaluadoInterface';
import EvaluadoInfo from '../../../../components/EvaludoInfo';
import { ThemeContext } from '../../../../components/ThemeContext/ThemeContext';
import { API } from '../../../../global/global';
import path from 'path';
import { icon } from 'leaflet';

export const ModulosListaPresnecialScreen = () => {
  const { evaluadosList_id } = useParams<{ evaluadosList_id: string }>();
  const { reset } = useForm();
  const [isOpen, setIsOpen] = useState(false);
  const toggleBlind = () => setIsOpen(!isOpen);
  const { isDarkMode } = useContext(ThemeContext);
  const [moduleStatus, setModuleStatus] = useState({
    domiciliaria: 'rojo', // Por defecto rojo (ningún archivo cargado)
    socioeconomica: 'rojo', // Por defecto rojo (ningún archivo cargado)
    formatos: 'rojo', // Por defecto rojo (ningún archivo cargado)
    documentos: 'rojo', // Por defecto rojo (ningún archivo cargado)
    psicologica: 'rojo', // Por defecto rojo (ningún archivo cargado)
    psicometrica: 'rojo', // Por defecto rojo (ningún archivo cargado)
    medica: 'rojo', // Por defecto rojo (ningún archivo cargado)
    toxicologica: 'rojo', // Por defecto rojo (ningún archivo cargado)
    deteccionRiesgos: 'rojo', // Por defecto rojo (ningún archivo cargado)
    analisisInformacion: 'rojo', // Por defecto rojo (ningún archivo cargado)
    otrasEvaluaciones: 'rojo', // Por defecto rojo (ningún archivo cargado)
    envioResultados: 'rojo', // Añadido para envío de resultados
    poligrafo: 'rojo',
    eyedetc: 'rojo',
    verieye: 'rojo',
    voice: 'rojo'
  });
  const [filesCount, setFilesCount] = useState({
    domiciliaria: { loaded: 0, total: 11 },
    socioeconomica: { loaded: 0, total: 10 },
    formatos: { loaded: 0, total: 6 },
    documentos: { loaded: 0, total: 35 },
    psicologica: { loaded: 0, total: 6 },
    psicometrica: { loaded: 0, total: 4 },
    medica: { loaded: 0, total: 4 },
    toxicologica: { loaded: 0, total: 4 },
    deteccionRiesgos: { loaded: 0, total: 5 }, 
    analisisInformacion: { loaded: 0, total: 2 },
    otrasEvaluaciones: { loaded: 0, total: 5 },
    envioResultados: { loaded: 0, total: 5 }, // Añadido para envío de resultados
    poligrafo: { loaded: 0, total: 1 },
    eyedetc: { loaded: 0, total: 1 },
    verieye: { loaded: 0, total: 1 },
    voice: { loaded: 0, total:1 }
  });

  useEffect(() => {
    const checkModulesStatus = async () => {
      if (!evaluadosList_id) return;
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API}/evaluado/${evaluadosList_id}/modulos-status`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        const data = await response.json();
        
        const mapModuleName = (backendName: string): string => {
          const mapping: Record<string, string> = {
            'visita_domiciliaria': 'domiciliaria',
            'evaluacion_socioeconomica': 'socioeconomica',
            'formatos_generales': 'formatos',
            'documentos_evaluado': 'documentos',
            'evaluacion_psicologica': 'psicologica',
            'evaluacion_psicometrica': 'psicometrica',
            'evaluacion_medica': 'medica',
            'evaluacion_toxicologica': 'toxicologica',
            'evaluacion_deteccion_riesgos': 'deteccionRiesgos',
            'analisis_informacion': 'analisisInformacion',
            'otras_evaluaciones': 'otrasEvaluaciones',
            'envio_resultadosPresencial': 'envioResultados',
            'poligrafia_presencial': 'poligrafo',
            'eyedetc_presencial': 'eyedetc',
            'verieye_presencial': 'verieye',
            'voice_stress_analysis': 'voice'
          };
          return mapping[backendName] || backendName;
        };
        
        const statusMap: Record<string, string> = {};
        const countMap: Record<string, { loaded: number; total: number }> = {};
        
        Object.entries(data).forEach(([key, value]: [string, any]) => {
          const frontendKey = mapModuleName(key);
          const loaded = value.count || 0;
          const total = value.total || 0;
          countMap[frontendKey] = { loaded, total };
          statusMap[frontendKey] = loaded === 0 ? 'rojo' : loaded < total ? 'amarillo' : 'verde';
        });
        
        setModuleStatus(statusMap as any);
        setFilesCount(countMap as any);
      } catch (error) {
        console.error("Error al verificar el estado de los archivos:", error);
      }
    };

    checkModulesStatus();
    
  }, [evaluadosList_id]);

  const modules = [
    { 
      title: 'Documentos', 
      icon: FileStack, 
      path: `/documentos_presencial/${evaluadosList_id}`,
      status: moduleStatus.documentos,
      filesCount: filesCount.documentos
    },
    { 
      title: 'Formatos Generales', 
      icon: FileText, 
      path: `/formatos_presencial/${evaluadosList_id}`,
      status: moduleStatus.formatos,
      filesCount: filesCount.formatos
    },
    { 
      title: 'Investigación Socioeconómica', 
      icon: ChartBar, 
      path: `/socioeconomica_presencial/${evaluadosList_id}`,
      status: moduleStatus.socioeconomica,
      filesCount: filesCount.socioeconomica
    },
    { 
      title: 'Visita Domiciliaria', 
      icon: Home, 
      path: `/domiciliaria_presencial/${evaluadosList_id}`,
      status: moduleStatus.domiciliaria,
      filesCount: filesCount.domiciliaria
    },
    { 
      title: 'Evaluación Psicológica', 
      icon: Brain, 
      path: `/psicologica_presencial/${evaluadosList_id}`,
      status: moduleStatus.psicologica,
      filesCount: filesCount.psicologica
    },
    { 
      title: 'Evaluación Psicométrica', 
      icon: ClipboardList, 
      path: `/psicometrica_presencial/${evaluadosList_id}`,
      status: moduleStatus.psicometrica,
      filesCount: filesCount.psicometrica 
    },
    { 
      title: 'Evaluación Médica', 
      icon: Stethoscope, 
      path: `/medica_presencial/${evaluadosList_id}`,
      status: moduleStatus.medica,
      filesCount: filesCount.medica 
    },
    { 
      title: 'Evaluación Toxicológica', 
      icon: Beaker, 
      path: `/toxicologica_presencial/${evaluadosList_id}`,
      status: moduleStatus.toxicologica,
      filesCount: filesCount.toxicologica 
    },
    { 
      title: 'Evaluación de Detección de Riesgos', 
      icon: Target, 
      path: `/poligrafo_presencial/${evaluadosList_id}`,
      status: moduleStatus.deteccionRiesgos,
      filesCount: filesCount.deteccionRiesgos
    },
    { 
      title: 'Reporte General del Área de Análisis de Información', 
      icon: Search, 
      path: `/informacion_presencial/${evaluadosList_id}`,
      status: moduleStatus.analisisInformacion,
      filesCount: filesCount.analisisInformacion
    },
    { 
      title: 'Otras Evaluaciones', 
      icon: ClipboardList, 
      path: `/otras_evaluaciones/${evaluadosList_id}`,
      status: moduleStatus.otrasEvaluaciones,
      filesCount: filesCount.otrasEvaluaciones
    },
    { 
      title: 'Envío de Resultados', 
      icon: ClipboardCopy, 
      path: `/envio_resultadosPresencial/${evaluadosList_id}`,
      status: moduleStatus.envioResultados,
      filesCount: filesCount.envioResultados
    },
    {
      title: 'Polígrafo',
      icon: Pen,
      path: `/poligrafia_presencial/${evaluadosList_id}`,
      status: moduleStatus.poligrafo,
      filesCount: filesCount.poligrafo
    },
    {
      title: 'Eyedetc',
      icon: ScanEye,
      path: `/eyedetc_presencial/${evaluadosList_id}`,
      status: moduleStatus.eyedetc,
      filesCount: filesCount.eyedetc
    },
    {
      title: 'VeriEye',
      icon: Eye,
      path: `/verieye_presencial/${evaluadosList_id}`,
      status: moduleStatus.verieye,
      filesCount: filesCount.verieye
    },
    {
      title: 'Voice Stress Analysis',
      icon: AudioLines,
      path: `/voice_stress_analysis/${evaluadosList_id}`,
      status: moduleStatus.voice,
      filesCount: filesCount.voice
    },
    { 
      title: 'Reporte Final', 
      icon: FileBarChart, 
      path: `/evaluaciones/presencial/reporte-final/${evaluadosList_id}`,
      status: 'verde',
      filesCount: undefined
    }
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: 'easeOut',
      },
    }),
  };

  const queryEvaluado = useGetData<EvaluadoInterface>({
    url: `/evaluado/${evaluadosList_id}`,
    name: ['evaluado'],
  });

  useEffect(() => {  
    if (queryEvaluado.data) {
      reset({
        empresa: queryEvaluado.data.empresa?.nombre || '',
      });
    }
  }, [queryEvaluado.data, reset]);

  // Estilos adaptativos según el tema
  const getBackgroundStyle = () => {
    return isDarkMode 
      ? "bg-gradient-to-br from-gray-900 to-gray-800" 
      : "bg-gradient-to-br from-cyan-800 to-cyan-500";
  };

  const getButtonStyle = () => {
    return isDarkMode
      ? "bg-gradient-to-r from-blue-600 to-blue-800 text-white py-3 rounded-lg focus:outline-none border-2 border-gray-700"
      : "bg-gradient-to-r from-cyan-500 to-cyan-500 text-white py-3 rounded-lg focus:outline-none border-2 border-white";
  };

  const getCancelButtonStyle = () => {
    return isDarkMode ? "bg-red-600 text-white" : "bg-red-500 text-white";
  };

  const getCardStyle = (module: any) => {
    // Si es uno de los módulos que queremos colorear según el estado
    if ((module.title === 'Visita Domiciliaria' || 
         module.title === 'Investigación Socioeconómica' || 
         module.title === 'Formatos Generales' ||
         module.title === 'Documentos' ||
         module.title === 'Evaluación Psicológica' ||
         module.title === 'Evaluación Psicométrica' ||
         module.title === 'Evaluación Médica' ||
         module.title === 'Evaluación Toxicológica' ||
         module.title === 'Evaluación de Detección de Riesgos' ||
         module.title === 'Reporte General del Área de Análisis de Información' ||
         module.title === 'Otras Evaluaciones' ||
         module.title === 'Polígrafo' ||
         module.title === 'Eyedetc' ||
         module.title === 'VeriEye' ||
         module.title === 'Voice Stress Analysis' ||
         module.title === 'Envío de Resultados') && module.status) {
      const borderColor = 
        module.status === 'verde' ? 'border-green-500' : 
        module.status === 'amarillo' ? 'border-yellow-300' : 
        'border-red-500';
      
      return isDarkMode 
        ? `bg-gray-800 rounded-lg shadow-lg overflow-hidden cursor-pointer h-full border-3 ${borderColor} relative` 
        : `bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer h-full border-3 ${borderColor} relative`;
    }
    
    // Para los demás módulos, el estilo normal
    return isDarkMode 
      ? "bg-gray-800 rounded-lg shadow-lg overflow-hidden cursor-pointer h-full border border-gray-700" 
      : "bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer h-full";
  };

  const getCounterBadgeStyle = (module: any) => {
    if ((module.title === 'Visita Domiciliaria' || 
         module.title === 'Investigación Socioeconómica' || 
         module.title === 'Formatos Generales' ||
         module.title === 'Documentos' ||
         module.title === 'Evaluación Psicológica' ||
         module.title === 'Evaluación Psicométrica' ||
         module.title === 'Evaluación Médica' ||
         module.title === 'Evaluación Toxicológica' ||
         module.title === 'Evaluación de Detección de Riesgos' ||
         module.title === 'Reporte General del Área de Análisis de Información' ||
         module.title === 'Otras Evaluaciones' ||
         module.title === 'Polígrafo' ||
         module.title === 'Eyedetc' ||
         module.title === 'VeriEye' ||
         module.title === 'Voice Stress Analysis' ||
         module.title === 'Envío de Resultados') && module.status) {
      const badgeColor = 
        module.status === 'verde' ? 'bg-green-500 text-white' : 
        module.status === 'amarillo' ? 'bg-yellow-300 text-gray-800' : 
        'bg-red-500 text-white';
      
      return `absolute top-2 right-2 ${badgeColor} rounded-full px-3 py-1 text-xs font-bold`;
    }
    return '';
  };

  const getIconStyle = () => {
    return isDarkMode ? "text-customCyan" : "text-indigo-500";
  };

  const getCardTitleStyle = () => {
    return isDarkMode ? "text-white" : "text-gray-800";
  };

  const getCardTextStyle = () => {
    return isDarkMode ? "text-gray-400" : "text-gray-600";
  };

  const getTitleStyle = () => {
    return isDarkMode ? "text-white" : "text-white";
  };

  // Agregar estilos para el borde del módulo según su estado
  const getBorderStyle = (module: any) => {
    if ((module.title === 'Visita Domiciliaria' || 
         module.title === 'Investigación Socioeconómica' || 
         module.title === 'Formatos Generales' ||
         module.title === 'Documentos' ||
         module.title === 'Evaluación Psicológica' ||
         module.title === 'Evaluación Psicométrica' ||
         module.title === 'Evaluación Médica' ||
         module.title === 'Evaluación Toxicológica' ||
         module.title === 'Evaluación de Detección de Riesgos' ||
         module.title === 'Reporte General del Área de Análisis de Información' ||
         module.title === 'Otras Evaluaciones' ||
         module.title === 'Polígrafo' ||
         module.title === 'Eyedetc' ||
         module.title === 'VeriEye' ||
         module.title === 'Voice Stress Analysis' ||
         module.title === 'Envío de Resultados') && module.status) {
      const borderStyle = 
        module.status === 'verde' ? 'ring-4 ring-green-500 ring-opacity-70' : 
        module.status === 'amarillo' ? 'ring-4 ring-yellow-300 ring-opacity-70' : 
        'ring-4 ring-red-500 ring-opacity-70';
      
      return borderStyle;
    }
    return '';
  };

  return (
    <div className={`min-h-screen ${getBackgroundStyle()} p-8`}>
      {/* Botón "Regresar" */}
      <div className="flex justify-between items-center mb-8">
        <Link to={`/tipoModulo/${evaluadosList_id}`} onClick={() => window.scrollTo(0, 0)}>
          <motion.button
            className={`${getCancelButtonStyle()} py-2 px-4 rounded-lg`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Regresar
          </motion.button>
        </Link>
      </div>

      {/* Título */}
      <h1 className={`text-4xl font-bold ${getTitleStyle()} mb-8 text-center flex-1`}>
        Modulos de evaluaciones presenciales
      </h1>

      {/* Botón de persiana */}
      <div className="flex justify-center">
        <motion.div className="mb-4 w-full max-w-[400px]">
          <motion.button
            onClick={toggleBlind}
            className={`w-[400px] text-lg font-semibold ${getButtonStyle()}`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isOpen ? 'Cerrar información del evaluado' : 'Información del evaluado'}
          </motion.button>
        </motion.div>
      </div>

      {/* Componente de información del evaluado con control externo */}
      <EvaluadoInfo 
        externalIsOpen={isOpen} 
        externalToggle={toggleBlind}
        hideButton={true} 
      />

      {/* Grid de módulos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
        {modules.map((module, index) => (
          <Link to={module.path} key={module.title} className="no-underline" onClick={() => window.scrollTo(0, 0)}>
            <motion.div
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              custom={index}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`${getCardStyle(module)} ${getBorderStyle(module)}`}
            >
              <div className="p-6 flex flex-col justify-between h-full">
                {(module.title === 'Visita Domiciliaria' || 
                  module.title === 'Investigación Socioeconómica' || 
                  module.title === 'Formatos Generales' ||
                  module.title === 'Documentos' ||
                  module.title === 'Evaluación Psicológica' ||
                  module.title === 'Evaluación Psicométrica' ||
                  module.title === 'Evaluación Médica' ||
                  module.title === 'Evaluación Toxicológica' ||
                  module.title === 'Evaluación de Detección de Riesgos' ||
                  module.title === 'Reporte General del Área de Análisis de Información' ||
                  module.title === 'Otras Evaluaciones' ||
                  module.title === 'Polígrafo' ||
                  module.title === 'Eyedetc' ||
                  module.title === 'VeriEye' ||
                  module.title === 'Voice Stress Analysis' ||
                  module.title === 'Envío de Resultados') && module.filesCount && (
                  <span className={getCounterBadgeStyle(module)}>
                    {module.filesCount.loaded}/{module.filesCount.total}
                  </span>
                )}
                <module.icon className={`w-12 h-12 ${getIconStyle()} mb-4`} />
                <h2 className={`text-xl font-semibold ${getCardTitleStyle()} mb-2`}>{module.title}</h2>
                <p className={getCardTextStyle()}>Click para ver detalles</p>
              </div>
            </motion.div>
          </Link>
        ))}
      </div>
    </div>
  );
};